import React from 'react';
import {
  EmailIcon,
  Container,
  TextInput,
  ChipContainer,
  Chip,
  ChipDelete,
} from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/components/email-autocomplete/style';
import ErrorMessage from 'components/error-message';
import { useTranslation } from 'i18n';
import { isValidEmail, notEmpty } from 'utils/js';
import { uniqBy, isEmpty } from 'lodash';
import { useFormikContext } from 'formik';
import SurfaceRolesDropdown, {
  Role,
} from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/components/email-autocomplete/components/surface-roles-dropdown';
import { SurfaceRole } from 'graphql/types';

export interface EmailAutocompleteProps {
  name: string;
  onChange?(sharedWith: SharedWithValue[]): void;
}

interface SharedWithValue {
  email: string;
  role: SurfaceRole;
}

interface UserEmail {
  email: string;
  invalid?: boolean;
}

const defaultProps = {};

function EmailAutocomplete(props: EmailAutocompleteProps) {
  const { t } = useTranslation();

  const inputRef = React.useRef<HTMLInputElement | undefined>();

  const [emails, setEmails] = React.useState<UserEmail[]>([]);
  const [role, setRole] = React.useState<Role | undefined>(undefined);

  const [inputValue, setInputValue] = React.useState('');

  const { setFieldValue, errors, setErrors } = useFormikContext();

  const errorMessage = errors[props.name];

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  React.useEffect(() => {
    const invalidEmails = emails
      .filter(({ invalid }) => invalid)
      .map(({ email }) => email);

    if (notEmpty(invalidEmails)) {
      setErrors({
        [props.name]: t('Invalid email format for {{emails}}', {
          replace: { emails: invalidEmails.join(', ') },
        }),
      });
    } else {
      setErrors({ [props.name]: undefined });
    }

    const sharedWith = emails.map(({ email }) => ({
      email,
      role: role?.value,
    }));

    setFieldValue(props.name, sharedWith);

    props.onChange && props.onChange(sharedWith as SharedWithValue[]);
  }, [emails]);

  function onClick() {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  function onRoleChange(role: { label: string; value: any }) {
    setRole(role);
  }

  function onInputChange(event) {
    setInputValue(event.target?.value);
  }

  function onChipDelete(email) {
    return function () {
      setEmails(emails.filter((data) => data.email !== email));
    };
  }

  function addEmail(email: string) {
    setInputValue('');

    const newValues = [
      ...emails,
      {
        email,
        invalid: isValidEmail(email) === false,
      },
    ];

    const uniqValues = uniqBy(newValues, 'email');

    setEmails(uniqValues);
  }

  const chipsWithMargin = emails.length > 1;

  const hasChips = notEmpty(emails);

  return (
    <>
      <Container $invalid={Boolean(errorMessage)} onClick={onClick}>
        <EmailIcon $hasChips={hasChips} />
        <ChipContainer $hasChips={hasChips}>
          {emails.map(({ email, invalid }) => (
            <Chip key={email} $invalid={invalid} withMargin={chipsWithMargin}>
              {email}
              <ChipDelete onClick={onChipDelete(email)} />
            </Chip>
          ))}

          <TextInput
            ref={inputRef}
            value={inputValue}
            onChange={onInputChange}
            placeholder={isEmpty(emails) ? t('Enter email address') : undefined}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') {
                const email = event.target.value;
                addEmail(email);
              }
            }}
          />
        </ChipContainer>

        <SurfaceRolesDropdown
          defaultValue={SurfaceRole.Viewer}
          onChange={onRoleChange}
        />
      </Container>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
}

EmailAutocomplete.defaultProps = defaultProps;

export default EmailAutocomplete;
